import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { ProductGrid } from "../product";
import { SEO } from "../SEO";

const PHOENIX_HERO_IMAGE = "https://customer-assets.emergentagent.com/job_57f3e4d8-87fc-4053-ba36-5d4ff7f3cc21/artifacts/fxw8nfqo_generated_image_20260430_163809_1.png";
const MONEY_QUEEN_HERO_IMAGE = "https://customer-assets.emergentagent.com/job_57f3e4d8-87fc-4053-ba36-5d4ff7f3cc21/artifacts/jnmfcxet_generated_image_20260427_173445_1.png";

function PhoenixHero({ onShopClick }) {
  return (
    <div
      className="relative overflow-hidden rounded-3xl border border-rose-500/20 bg-gradient-to-br from-zinc-950 via-rose-950/40 to-amber-950/30 mb-16 shadow-[0_0_60px_-15px_rgba(244,63,94,0.4)]"
      data-testid="phoenix-hero-banner"
    >
      {/* Decorative grain / glow */}
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 80% 30%, rgba(251,191,36,0.25), transparent 50%), radial-gradient(circle at 15% 85%, rgba(244,63,94,0.25), transparent 55%)" }} />
      <div className="absolute -top-32 -right-16 w-[28rem] h-[28rem] rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-16 w-80 h-80 rounded-full bg-rose-500/10 blur-3xl pointer-events-none" />

      <div className="relative grid md:grid-cols-2 gap-8 md:gap-12 items-center p-8 sm:p-12 md:p-16">
        {/* Copy */}
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-400/30">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
            <span className="text-rose-200 text-xs tracking-[0.25em] uppercase font-semibold">New Drop · Phoenix Capsule</span>
          </div>

          <h1 className="text-white font-bebas leading-[0.95] text-5xl sm:text-6xl md:text-7xl">
            I Celebrate
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-rose-300 to-amber-200"> Me.</span>
          </h1>

          <p className="text-white/70 text-base sm:text-lg max-w-md leading-relaxed">
            Rise like a phoenix. Seven new empowerment tees built for the woman who breaks chains, blooms in fire, and wears her crown loud.
          </p>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              onClick={onShopClick}
              className="group px-7 py-3.5 bg-gradient-to-r from-amber-400 to-rose-500 text-black font-bold rounded-full text-sm tracking-wide hover:from-amber-300 hover:to-rose-400 transition-all shadow-lg shadow-rose-500/30 hover:scale-[1.03]"
              data-testid="phoenix-shop-now"
            >
              Shop The Capsule
              <span className="inline-block ml-2 transition-transform group-hover:translate-x-1">→</span>
            </button>
            <div className="flex items-center gap-3 text-xs text-white/50 pl-2">
              <span className="px-2 py-1 rounded bg-white/5 border border-white/10">7 styles</span>
              <span className="px-2 py-1 rounded bg-white/5 border border-white/10">From $32.39</span>
            </div>
          </div>

          {/* Empowerment ribbons */}
          <div className="flex flex-wrap gap-2 pt-4">
            {["Resilience", "Rebirth", "Royalty", "Self-Love"].map((word) => (
              <span key={word} className="text-[11px] tracking-widest uppercase text-amber-200/70 px-2.5 py-1 border border-amber-300/20 rounded-sm">
                {word}
              </span>
            ))}
          </div>
        </div>

        {/* Image */}
        <div className="relative">
          <div className="relative aspect-square max-w-md mx-auto">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-amber-400/20 via-transparent to-rose-500/20 blur-xl" />
            <img
              src={PHOENIX_HERO_IMAGE}
              alt="I Celebrate Me Phoenix Tee"
              loading="eager"
              className="relative w-full h-full object-contain drop-shadow-[0_25px_45px_rgba(244,63,94,0.35)]"
              data-testid="phoenix-hero-image"
            />
            {/* Floating accent badge */}
            <div className="absolute -top-3 -right-3 sm:top-4 sm:-right-2 px-3 py-1.5 rounded-full bg-black border border-amber-300/40 text-amber-200 text-[11px] font-bold tracking-wider shadow-lg shadow-amber-500/10">
              ✦ EMPOWERMENT EDIT
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MoneyQueenHero({ onShopClick }) {
  return (
    <div
      className="relative overflow-hidden rounded-3xl border border-amber-400/30 bg-gradient-to-br from-zinc-950 via-zinc-900 to-yellow-950/30 mb-16 shadow-[0_0_70px_-15px_rgba(251,191,36,0.45)]"
      data-testid="money-queen-hero-banner"
    >
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 85% 25%, rgba(251,191,36,0.3), transparent 55%), radial-gradient(circle at 12% 90%, rgba(34,197,94,0.12), transparent 60%)" }} />
      <div className="absolute -top-32 -right-20 w-[32rem] h-[32rem] rounded-full bg-amber-500/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-16 w-80 h-80 rounded-full bg-yellow-500/10 blur-3xl pointer-events-none" />

      <div className="relative grid md:grid-cols-2 gap-8 md:gap-12 items-center p-8 sm:p-12 md:p-16">
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/10 border border-amber-300/40">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
            <span className="text-amber-200 text-xs tracking-[0.25em] uppercase font-semibold">New · Money Queen Capsule</span>
          </div>

          <h1 className="text-white font-bebas leading-[0.92] text-5xl sm:text-6xl md:text-7xl">
            Sheesh!!
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-amber-200 to-yellow-400" style={{ filter: "drop-shadow(0 4px 16px rgba(251,191,36,0.4))" }}> Run It.</span>
          </h1>

          <p className="text-white/70 text-base sm:text-lg max-w-md leading-relaxed">
            Hoes Mad. Sheesh. Chasin' Paper Daily. Gold-drip statement tees for the woman who runs it — money on her mind, crown on her head.
          </p>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              onClick={onShopClick}
              className="group px-7 py-3.5 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold rounded-full text-sm tracking-wide hover:from-yellow-300 hover:to-amber-400 transition-all shadow-lg shadow-amber-500/30 hover:scale-[1.03]"
              data-testid="money-queen-shop-now"
            >
              Shop The Capsule
              <span className="inline-block ml-2 transition-transform group-hover:translate-x-1">→</span>
            </button>
            <div className="flex items-center gap-3 text-xs text-white/50 pl-2">
              <span className="px-2 py-1 rounded bg-white/5 border border-white/10">4 styles</span>
              <span className="px-2 py-1 rounded bg-white/5 border border-white/10">From $32.39</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-4">
            {["Hustle", "Drip", "Crown", "Run It Up"].map((word) => (
              <span key={word} className="text-[11px] tracking-widest uppercase text-amber-200/70 px-2.5 py-1 border border-amber-300/20 rounded-sm">
                {word}
              </span>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="relative aspect-square max-w-md mx-auto">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-400/20 via-transparent to-amber-500/25 blur-xl" />
            <img
              src={MONEY_QUEEN_HERO_IMAGE}
              alt="Sheesh Money Queen Tee"
              loading="eager"
              className="relative w-full h-full object-contain drop-shadow-[0_25px_45px_rgba(251,191,36,0.4)]"
              data-testid="money-queen-hero-image"
            />
            <div className="absolute -top-3 -right-3 sm:top-4 sm:-right-2 px-3 py-1.5 rounded-full bg-black border border-amber-300/50 text-amber-200 text-[11px] font-bold tracking-wider shadow-lg shadow-amber-500/20">
              ✦ MONEY MOVES
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Capsule definitions: URL slug -> filter rules + SEO copy
const CAPSULES = {
  all: {
    slug: "all",
    label: "All",
    active: "from-pink-500 to-rose-500",
    seoTitle: "Ladies Streetwear — Bold, Feminine, Unapologetic",
    seoDesc: "Fresh Po'Boy Ladies Collection. Empowerment streetwear, money-queen tees, and Phoenix capsule for her. Premium designs in T-shirts, crop tops, and hoodies.",
    ogImage: PHOENIX_HERO_IMAGE,
    hero: "phoenix",
    match: () => true,
  },
  phoenix: {
    slug: "phoenix",
    label: "Phoenix",
    active: "from-amber-400 to-rose-500",
    seoTitle: "I Celebrate Me Phoenix Capsule — Ladies Empowerment Tees",
    seoDesc: "The I Celebrate Me Phoenix capsule. Rise like a phoenix in bold gold-drip empowerment streetwear for her. Black, white, charcoal — Resilience · Rebirth · Royalty · Self-Love.",
    ogImage: PHOENIX_HERO_IMAGE,
    hero: "phoenix",
    match: (p) => /phoenix|celebrate me/i.test(p.name || ""),
  },
  "money-queen": {
    slug: "money-queen",
    label: "Money Queen",
    active: "from-yellow-400 to-amber-500",
    seoTitle: "Money Queen Capsule — Sheesh, Hoes Mad & Chasin' Paper Tees",
    seoDesc: "Money Queen ladies streetwear capsule. Sheesh!! Hoes Mad. Chasin' Paper Daily. Bold gold-drip statement tees for the woman who runs it.",
    ogImage: MONEY_QUEEN_HERO_IMAGE,
    hero: "money",
    match: (p) => /(hoes mad|sheesh|chasin|paper|money)/i.test(p.name || ""),
  },
  other: {
    slug: "other",
    label: "Other",
    active: "from-pink-400 to-rose-400",
    seoTitle: "Ladies Streetwear — Bonus Drops",
    seoDesc: "Fresh Po'Boy Ladies streetwear — bonus drops outside our signature capsules.",
    ogImage: PHOENIX_HERO_IMAGE,
    hero: null,
    match: (p) => !/phoenix|celebrate me|hoes mad|sheesh|chasin|paper|money/i.test(p.name || ""),
  },
};

const CAPSULE_ORDER = ["all", "phoenix", "money-queen", "other"];

export function LadiesSection({ products, isPage }) {
  const navigate = useNavigate();
  const { capsule: capsuleParam } = useParams();

  // Resolve current capsule from URL (?supports old /:capsule and home /streetwear/ladies)
  const initialCapsule = capsuleParam && CAPSULES[capsuleParam] ? capsuleParam : "all";
  const [filter, setFilter] = useState(initialCapsule);
  const [colorFilter, setColorFilter] = useState("all"); // 'all' | 'Black' | 'White' | 'Charcoal'

  // Sync state when URL changes (e.g. user clicks a capsule pill that navigates)
  useEffect(() => {
    setFilter(capsuleParam && CAPSULES[capsuleParam] ? capsuleParam : "all");
  }, [capsuleParam]);

  const ladiesProducts = useMemo(() => products.filter(
    p => p.collection === "Ladies" || (p.category || "").toLowerCase().includes("ladies")
  ), [products]);

  const counts = useMemo(() => {
    const c = { all: ladiesProducts.length };
    for (const slug of CAPSULE_ORDER) {
      if (slug === "all") continue;
      c[slug] = ladiesProducts.filter(CAPSULES[slug].match).length;
    }
    return c;
  }, [ladiesProducts]);

  // Available colors across the current capsule
  const availableColors = useMemo(() => {
    const capsule = CAPSULES[filter] || CAPSULES.all;
    const inCapsule = ladiesProducts.filter(capsule.match);
    const set = new Set();
    inCapsule.forEach(p => (p.colors || []).forEach(c => c && set.add(c)));
    return Array.from(set);
  }, [ladiesProducts, filter]);

  // Reset color filter when capsule changes if the chosen color isn't available
  useEffect(() => {
    if (colorFilter !== "all" && !availableColors.includes(colorFilter)) {
      setColorFilter("all");
    }
  }, [availableColors, colorFilter]);

  const visibleProducts = useMemo(() => {
    const capsule = CAPSULES[filter] || CAPSULES.all;
    let list = ladiesProducts.filter(capsule.match);
    if (colorFilter !== "all") {
      list = list.filter(p => (p.colors || []).includes(colorFilter));
    }
    return list;
  }, [ladiesProducts, filter, colorFilter]);

  const handleCapsuleClick = (slug) => {
    setFilter(slug);
    if (slug === "all") navigate("/streetwear/ladies");
    else navigate(`/streetwear/ladies/${slug}`);
  };

  const filters = CAPSULE_ORDER
    .map(slug => ({ ...CAPSULES[slug], count: counts[slug] || 0 }))
    .filter(f => f.slug === "all" || f.count > 0);

  const currentCapsule = CAPSULES[filter] || CAPSULES.all;

  const scrollToProducts = () => {
    const grid = document.getElementById("ladies-product-grid");
    if (grid) grid.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className={`py-20 px-4 ${isPage ? 'pt-28' : ''} bg-gradient-to-b from-black via-pink-950/10 to-black`} data-testid="ladies-section">
      <div className="max-w-7xl mx-auto">
        {isPage && (
          <SEO
            title={currentCapsule.seoTitle}
            description={currentCapsule.seoDesc}
            image={currentCapsule.ogImage}
            path={filter === "all" ? "/streetwear/ladies" : `/streetwear/ladies/${filter}`}
          />
        )}

        {isPage && ladiesProducts.length > 0 && currentCapsule.hero === "phoenix" && (
          <PhoenixHero onShopClick={scrollToProducts} />
        )}
        {isPage && ladiesProducts.length > 0 && currentCapsule.hero === "money" && (
          <MoneyQueenHero onShopClick={scrollToProducts} />
        )}

        <div className="text-center mb-12" id="ladies-product-grid">
          <span className="text-pink-400 text-sm tracking-widest uppercase font-semibold">For Her</span>
          <h2 className="text-white text-5xl sm:text-6xl font-bebas mt-2">
            Ladies <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-rose-400 to-red-400">Streetwear</span>
          </h2>
          <p className="text-white/50 mt-4 max-w-xl mx-auto">Bold, feminine, and unapologetic. Premium streetwear designed for her.</p>
        </div>

        {/* Navigation back to main Streetwear */}
        {isPage && (
          <div className="flex justify-center mb-8">
            <button
              onClick={() => navigate("/streetwear")}
              className="px-5 py-2 border border-white/20 text-white/70 rounded-lg text-sm hover:bg-white/5 transition-all"
              data-testid="back-to-streetwear"
            >
              View All Streetwear
            </button>
          </div>
        )}

        {/* Capsule filter pills (only on the dedicated page) */}
        {isPage && ladiesProducts.length > 0 && (
          <>
            <div className="flex flex-wrap justify-center gap-3 mb-4" data-testid="ladies-filter-bar">
              {filters.map((f) => {
                const selected = filter === f.slug;
                return (
                  <button
                    key={f.slug}
                    onClick={() => handleCapsuleClick(f.slug)}
                    className={`group relative px-5 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-all border ${
                      selected
                        ? `bg-gradient-to-r ${f.active} text-black border-transparent shadow-lg shadow-rose-500/20 scale-[1.03]`
                        : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10 hover:text-white"
                    }`}
                    data-testid={`ladies-filter-${f.slug}`}
                  >
                    {f.label}
                    <span className={`ml-2 text-xs ${selected ? "text-black/70" : "text-white/40"}`}>
                      {f.count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Color filter row (secondary). Only shows when there's >1 color available */}
            {availableColors.length > 1 && (
              <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="ladies-color-filter-bar">
                <span className="text-white/40 text-xs uppercase tracking-widest self-center mr-1">Color</span>
                {["all", ...availableColors].map((c) => {
                  const selected = colorFilter === c;
                  return (
                    <button
                      key={c}
                      onClick={() => setColorFilter(c)}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-all border ${
                        selected
                          ? "bg-white text-black border-transparent"
                          : "bg-transparent text-white/60 border-white/15 hover:text-white hover:border-white/40"
                      }`}
                      data-testid={`ladies-color-${c.toLowerCase()}`}
                    >
                      {c === "all" ? "All Colors" : c}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        <ProductGrid products={visibleProducts} />

        {/* No results in current filter */}
        {isPage && ladiesProducts.length > 0 && visibleProducts.length === 0 && (
          <div className="text-center py-12 text-white/50" data-testid="ladies-no-filter-results">
            No products in this capsule yet. Try another filter.
          </div>
        )}

        {/* Empty State */}
        {ladiesProducts.length === 0 && (
          <div className="text-center py-16">
            <div className="bg-zinc-900/50 rounded-2xl p-12 max-w-2xl mx-auto border border-pink-500/20">
              <h3 className="text-white text-2xl font-bebas mb-4">Coming Soon</h3>
              <p className="text-white/60 mb-6">New ladies designs are dropping soon. Stay tuned!</p>
              <button onClick={() => navigate("/streetwear")} className="px-6 py-3 bg-pink-600 text-white font-semibold hover:bg-pink-500 transition-colors rounded-lg">
                Shop Streetwear
              </button>
            </div>
          </div>
        )}

        {/* View Ladies button when shown as sub-section */}
        {!isPage && ladiesProducts.length > 0 && (
          <div className="text-center mt-12">
            <button onClick={() => navigate("/streetwear/ladies")}
              className="px-8 py-4 bg-gradient-to-r from-pink-600 to-rose-500 text-white font-semibold hover:from-pink-500 hover:to-rose-400 transition-all rounded-lg"
              data-testid="view-ladies-btn">
              Shop Ladies Collection
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
